import React, { useCallback, useEffect, useState, useContext } from 'react';
import _ from 'lodash';
import appConfig from 'app-config';
import PropTypes from 'prop-types';

import { Flex } from '@jutro/layout';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';

import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { WMICDropdownMenuButton, WMICScrollToError } from 'wmic-pe-components-platform-react';

import { CONSTANTS, WMICValidationUtil, WMICVariousUtil, FlowStepId } from 'wmic-pe-portals-utils-js';
import { useWizardModals, useDocumentTitle, WMICWizardSubmissionPage } from 'wmic-pe-portals-wizard-components-ui';
import { WMICGLLiabilityCoverages, WMICGLLiabilityDetails } from 'wmic-pe-capability-gateway-quoteandbind-gl-react';
import { WMICCPAdditionalInterestDetailView } from 'wmic-pe-capability-gateway-quoteandbind-cp-react';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';

import metadata from './WMICGLLiabilityPage.metadata.json5';
import messages from './WMICGLLiabilityPage.messages';
import styles from './WMICGLLiabilityPage.module.scss';
import { TranslatorContext } from '@jutro/legacy/locale';

const structureCustomQuote = (submissionVM, affectedQuote, clauses) =>
    // convert OfferingDTO to CustomQuotedDTO structure
    ({
        quote: affectedQuote,
        quoteID: submissionVM.quoteID.value,
        sessionUUID: submissionVM.sessionUUID.value,
        periodStart: submissionVM.baseData.periodStartDate.value,
        periodEnd: submissionVM.baseData.periodEndDate.value,
        coverages: clauses,
    });

const getCustomQuote = (vm, lobPath, lobName, filterChangedClauses = false) => {
    const lobOffering = _.get(vm, `${lobPath}.value`);

    let clausesToUpdate = {
        [lobName]: lobOffering.coverages,
    };

    if (filterChangedClauses) {
        clausesToUpdate = ClausesUtil.structureClausesForServer(lobOffering.coverages, lobName, null);
    }

    return structureCustomQuote(vm, lobOffering, clausesToUpdate);
};

function WMICGLLiabilityPage(props) {
    const { wizardData: submissionVM, updateWizardData, underwritingIssues } = props;
    const translator = useContext(TranslatorContext);
    const [scrollToError, setScrollToError] = useState(false); // we toggle this when we want to scroll to the first error on the page

    const { CustomQuoteService } = useDependencies('CustomQuoteService');
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { setWizardLoading, showConfirm } = useWizardModals();
    const { authHeader } = useAuthentication();
    const {
        onValidate,
        disregardFieldValidation,
        initialValidation,
        registerInitialComponentValidation,
        isComponentValid,
    } = useValidation('WMICGLLiabilityPage');
    const viewModelService = useContext(ViewModelServiceContext);
    const [showErrorsAddlIns, setShowErrorsAddlIns] = useState(false);

    useDocumentTitle(translator(messages.wizard), submissionVM);

    const validateTableForm = useCallback(
        () =>
            WMICValidationUtil.validatePageEntities(submissionVM, [
                'lobData.generalLiability.policyDetails',
                'lobData.generalLiability.additionalInsureds',
            ]) && !WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.CP_LIABILITY),
        [submissionVM]
    );

    useEffect(() => {
        registerInitialComponentValidation(validateTableForm);
    }, [registerInitialComponentValidation, validateTableForm]);

    useEffect(() => {
        submissionVM.value.flowStepId_WMIC = FlowStepId.CP_LIABILITY;
    }, [submissionVM.value]);

    const getRemovedFieldsForLob = useCallback(
        (lobName, lobPath, newSubmissionVM) => {
            let allRemovedFields = [];

            const removedFieldsFromPropertyLineCoverages = ClausesUtil.getRemovedClausesID(
                submissionVM,
                newSubmissionVM,
                `${lobPath}.coverages.commercialGeneralLiabilityCoverages`
            );

            allRemovedFields = [...removedFieldsFromPropertyLineCoverages];

            return allRemovedFields;
        },
        [submissionVM]
    );

    const onClauseChange = useCallback(
        _basePath => {
            const lobPath = 'lobData.generalLiability.offerings.children[0]';
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, lobName, true);

            return CustomQuoteService.updateCustomQuoteCoveragesAndQuoteData(
                customQuote,
                submissionVM.value,
                authHeader
            ).then(response => {
                const newSubmissionVM = viewModelService.clone(submissionVM);
                newSubmissionVM.value = response;

                const allRemovedFields = getRemovedFieldsForLob(lobName, lobPath, newSubmissionVM);

                disregardFieldValidation(allRemovedFields);
                updateWizardData(newSubmissionVM);
            });
        },
        [
            submissionVM,
            CustomQuoteService,
            authHeader,
            viewModelService,
            disregardFieldValidation,
            updateWizardData,
            getRemovedFieldsForLob,
        ]
    );

    const syncCoverages = useCallback(
        (value, changedPath) => {
            const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
            return onClauseChange(basePath);
        },
        [onClauseChange]
    );

    const changeSubmission = useCallback(
        (value, changedPath) => {
            updateWizardData(ClausesUtil.setClauseValue(submissionVM, value, changedPath));
        },
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [submissionVM]
    );

    const changeSubmissionAndSync = useCallback(
        (value, changedPath) => {
            changeSubmission(value, changedPath);
            return syncCoverages(value, changedPath);
        },
        [changeSubmission, syncCoverages]
    );

    const renderAddAIButton = useCallback(
        ({ isEditing, onClick }) => (
            <Flex className="gw-mb-6" justifyContent="left">
                <WMICDropdownMenuButton
                    id="addNewAdditionalInsuredButton"
                    type="secondary"
                    size="medium"
                    buttonText={translator(messages.addAdditionalInsured)}
                    alignRight
                    onItemClick={onClick}
                    items={[
                        {
                            id: 'newPerson',
                            text: translator(messages.aiPerson),
                            code: _.lowerCase(CONSTANTS.Person),
                            icon: 'mi-person',
                        },
                        {
                            id: 'newCompany',
                            text: translator(messages.aiCompany),
                            code: _.lowerCase(CONSTANTS.Company),
                            icon: 'mi-home',
                        },
                    ]}
                    disabled={isEditing}
                />
            </Flex>
        ),
        []
    );

    const toCreateClicked = useCallback(
        index => {
            const additionalInsuredAddress = {
                country: CONSTANTS.COUNTRY.CA,
                city: '',
                province: '',
                postalCode: '',
            };
            const newVM = viewModelService.create(
                {
                    contactType: index,
                    locationAddress: additionalInsuredAddress,
                    isEditable: true,
                    subType: CONSTANTS.POLICY_ADDL_INSURED,
                },
                'pc',
                'wmic.edge.ca.capabilities.policyjob.lob.commercialproperty.coverables.dto.CPAdditionalInterest_WMICDTO',
                submissionVM.aspects.context()
            );

            return newVM;
        },
        [viewModelService, submissionVM.aspects]
    );

    const saveSubmission = useCallback(
        async newSubmission => {
            setWizardLoading(true);
            _.unset(newSubmission, 'errorsAndWarnings');

            return LoadSaveService.updateDraftSubmission(newSubmission, authHeader)
                .then(result => {
                    submissionVM.value = result;
                    updateWizardData(submissionVM);

                    if (WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.CP_LIABILITY)) {
                        WMICVariousUtil.scrollToTop();
                    }

                    return true;
                })
                .finally(() => {
                    setWizardLoading(false);
                });
        },
        [LoadSaveService, authHeader, setWizardLoading, submissionVM, updateWizardData]
    );

    const onSaveClickedAddtionalInsured = useCallback(
        async (additionalInsuredVM, index, isAdding) => {
            if (additionalInsuredVM.aspects.valid && additionalInsuredVM.aspects.subtreeValid) {
                const additionalInsureds = _.get(submissionVM, 'lobData.generalLiability.additionalInsureds.value');
                if (isAdding) {
                    additionalInsureds.push(additionalInsuredVM.value);
                } else if (additionalInsuredVM.publicID.value != null) {
                    additionalInsureds[index] = additionalInsuredVM.value;
                }
                return saveSubmission(submissionVM.value).then(result => {
                    setShowErrorsAddlIns(false);
                    return result;
                });
            }
            setShowErrorsAddlIns(true);
        },
        [saveSubmission, submissionVM]
    );

    const getName = useCallback(additionalInsured => {
        if (additionalInsured.contactType.value.code === _.lowerCase(CONSTANTS.Person)) {
            const firstName = additionalInsured.firstName.value;
            const lastName = additionalInsured.lastName.value;
            return `${firstName} ${lastName}`;
        }
        if (additionalInsured.contactType.value.code === _.lowerCase(CONSTANTS.Company)) {
            const company = additionalInsured.companyName.value;
            return company;
        }
    }, []);

    const onDeleteAdditionalInsured = useCallback(
        async (additionalInsuredVM, index) => {
            const name = getName(additionalInsuredVM);
            const response = await showConfirm({
                title: translator(messages.removeAdditionalInsuredShort),
                message: translator(messages.removeAdditionalInsuredLong, { displayName: name }),
            });
            if (response === CONSTANTS.MODAL_RESULT.CONFIRM) {
                const newSubmission = _.cloneDeep(submissionVM.value);
                const { additionalInsureds } = newSubmission.lobData.generalLiability;
                additionalInsureds.splice(index, 1);
                return saveSubmission(newSubmission);
            }
        },
        [getName, saveSubmission, showConfirm, submissionVM.value, translator]
    );

    const commonOverrides = {
        quoteID: submissionVM.quoteID.value,
        changeSubmissionAndSync,
        changeSubmission,
        onClauseChange,
        syncCoverages,
        underwritingIssues,
        onValidate,
    };

    const overrideProps = {
        liabilityCoverages: {
            ...commonOverrides,
            jobVM: submissionVM,
        },
        liabilityDetails: {
            updateWizardData,
            onValidate,
            jobVM: submissionVM,
        },
        additionalInsuredListView: {
            clickable: true,
            startOpen: true,
            readOnly: false,
            editEnabled: true,
            hideBorder: true,
            value: _.get(submissionVM, `lobData.generalLiability.additionalInsureds.children`, []),
            VMData: [
                {
                    headerText: translator(messages.name),
                    getData: getName,
                },
                {
                    headerText: translator(messages.numberOfDaysNotice),
                    path: 'numberOfDays',
                },
            ],
            detailViewComponent: WMICCPAdditionalInterestDetailView,
            detailViewComponentProps: {
                jobVM: submissionVM,
                showErrorsAddlInt: showErrorsAddlIns,
                updateWizardData,
            },
            onValidate,
            renderAddButton: renderAddAIButton,
            deferCreateUntilSave: true,
            toCreate: toCreateClicked,
            onCancel: isAdding => {
                if (isAdding) {
                    setShowErrorsAddlIns(false);
                }
            },
            onDelete: onDeleteAdditionalInsured,
            onSave: onSaveClickedAddtionalInsured,
        },
    };

    const onNext = useCallback(async () => {
        try {
            if (!isComponentValid) {
                setScrollToError(Date.now());
                return false;
            }

            setWizardLoading(true);
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.unset(newSubmissionVM.value, 'bindData');

            submissionVM.value = await LoadSaveService.updateDraftSubmissionWithLOBData(
                newSubmissionVM.value,
                authHeader
            );

            if (WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.CP_LIABILITY)) {
                WMICVariousUtil.scrollToTop();
                return false;
            }

            return submissionVM;
        } finally {
            setWizardLoading(false);
        }
    }, [LoadSaveService, authHeader, isComponentValid, setWizardLoading, submissionVM, viewModelService]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onChangeSubmissionAndSync: changeSubmissionAndSync,
            onChangeSubmission: changeSubmission,
            onSyncCoverages: syncCoverages,
        },
        resolveComponentMap: {
            WMICGLLiabilityCoverages,
            WMICGLLiabilityDetails,
        },
    };

    return (
        <WMICWizardSubmissionPage
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            cancelLabel={commonMessages.saveAndExit}
            onNext={onNext}
            flowStepId={FlowStepId.CP_LIABILITY}
        >
            <WMICScrollToError counter={scrollToError} />
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}
            />
        </WMICWizardSubmissionPage>
    );
}

WMICGLLiabilityPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
    }).isRequired,
    ...wizardProps,
};

export default WMICGLLiabilityPage;
